import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { 
  Leaf, ArrowLeft, CheckCircle, Flame, Thermometer, Snowflake, 
  Euro, Phone, CalendarDays, Clock, Package, Building2,
  FileText, ExternalLink, AlertCircle, ChevronDown, ChevronUp, Info,
  Shield, UserCheck, Award, ClipboardCheck, Gift, Download, ClipboardList, Edit, Calculator,
  Bell, Accessibility, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { downloadResultsPDF } from "@/utils/pdfGenerator";
import AidCalculatorModal from "@/components/AidCalculatorModal";
import RenoComparisonSection from "@/components/RenoComparisonSection";
import AidSimulatorSection from "@/components/AidSimulatorSection";
import AidHistorySection from "@/components/AidHistorySection";
import MiniAuditSection from "@/components/MiniAuditSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResultatEligibilite() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [detailedResults, setDetailedResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wantAidLoading, setWantAidLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingMode, setBookingMode] = useState(null);
  const [callbackTime, setCallbackTime] = useState("");
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [showWhyFree, setShowWhyFree] = useState(false);
  const [calculatorModal, setCalculatorModal] = useState({ isOpen: false, program: null });
  const [calculatedAmounts, setCalculatedAmounts] = useState({});
  
  // Modal pour modifier les revenus
  const [incomeModalOpen, setIncomeModalOpen] = useState(false);
  const [incomeForm, setIncomeForm] = useState({ income_rfr: "", household_size: "" });
  const [incomeUpdateLoading, setIncomeUpdateLoading] = useState(false);
  
  // Sections explicatives repliables (fermées par défaut)
  const [showEcretementInfo, setShowEcretementInfo] = useState(false);
  const [showResteChargeInfo, setShowResteChargeInfo] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leadResponse, detailedResponse, slotsResponse] = await Promise.all([
          axios.get(`${API}/leads/${leadId}`),
          axios.get(`${API}/leads/${leadId}/detailed-results`),
          axios.get(`${API}/appointments/available`)
        ]);
        setLead(leadResponse.data);
        setDetailedResults(detailedResponse.data);
        setAvailableSlots(slotsResponse.data.slots || []);
      } catch (error) {
        toast.error("Impossible de charger vos résultats");
        navigate("/");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [leadId, navigate]);

  const handleWantAid = async () => {
    setWantAidLoading(true);
    try {
      await axios.put(`${API}/leads/${leadId}/want-aid`);
      setLead({ ...lead, wants_aid: true });
      toast.success("Votre demande a été enregistrée ! Nous vous contacterons très bientôt.");
    } catch (error) {
      toast.error("Une erreur est survenue");
    } finally {
      setWantAidLoading(false);
    }
  };

  const handleBookSlot = async () => {
    if (bookingMode === 'slot' && !selectedSlot) {
      toast.error("Veuillez sélectionner un créneau");
      return;
    }
    if (bookingMode === 'callback' && !callbackTime) {
      toast.error("Veuillez indiquer votre préférence horaire");
      return;
    }

    setBookingLoading(true);
    try {
      if (bookingMode === 'slot') {
        await axios.post(`${API}/appointments/book`, {
          slot_id: selectedSlot,
          lead_id: leadId
        });
        toast.success("Rendez-vous confirmé !");
      } else {
        await axios.post(`${API}/appointments/callback-request?lead_id=${leadId}&preferred_time=${encodeURIComponent(callbackTime)}`);
        toast.success("Demande de rappel enregistrée !");
      }
      
      setBookingDialogOpen(false);
      const response = await axios.get(`${API}/leads/${leadId}`);
      setLead(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la réservation");
    } finally {
      setBookingLoading(false);
    }
  };

  // Ouvrir la modal de revenus avec les valeurs actuelles
  const openIncomeModal = () => {
    setIncomeForm({
      income_rfr: lead.income_rfr?.toString() || "",
      household_size: lead.household_size?.toString() || ""
    });
    setIncomeModalOpen(true);
  };

  // Mettre à jour les revenus et recalculer l'éligibilité
  const handleIncomeUpdate = async () => {
    if (!incomeForm.income_rfr || !incomeForm.household_size) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setIncomeUpdateLoading(true);
    try {
      // Mettre à jour le lead avec les nouvelles infos de revenus
      await axios.put(`${API}/leads/${leadId}/income`, {
        income_rfr: parseInt(incomeForm.income_rfr),
        household_size: parseInt(incomeForm.household_size)
      });

      // Recharger les données
      const [leadResponse, detailedResponse] = await Promise.all([
        axios.get(`${API}/leads/${leadId}`),
        axios.get(`${API}/leads/${leadId}/detailed-results`)
      ]);
      
      setLead(leadResponse.data);
      setDetailedResults(detailedResponse.data);
      setIncomeModalOpen(false);
      toast.success("Vos informations ont été mises à jour et votre éligibilité recalculée !");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setIncomeUpdateLoading(false);
    }
  };

  const getScoreColor = (label) => {
    switch (label) {
      case "chaud": return "bg-emerald-500";
      case "tiede": return "bg-amber-500";
      case "froid": return "bg-blue-500";
      default: return "bg-slate-500";
    }
  };

  const getScoreIcon = (label) => {
    switch (label) {
      case "chaud": return <Flame className="h-5 w-5" />;
      case "tiede": return <Thermometer className="h-5 w-5" />;
      case "froid": return <Snowflake className="h-5 w-5" />;
      default: return <Thermometer className="h-5 w-5" />;
    }
  };

  // Fonction unifiée pour le badge d'éligibilité
  // RÈGLE: "confirmé" uniquement si:
  // 1. eligibility_status === "eligible"
  // 2. is_calculable === true (ou undefined pour rétrocompatibilité)
  // 3. missing_fields vide
  // 4. amount !== null (montant calculé)
  const getEligibilityBadge = (status, missingFields = [], isCalculable = true, amount = undefined) => {
    // Cas "confirmé" = toutes conditions remplies
    const isConfirmed = status === "eligible" && 
                        isCalculable !== false && 
                        (missingFields || []).length === 0 &&
                        amount !== null;
    
    if (status === "eligible" && isConfirmed) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
          <CheckCircle className="h-3 w-3" /> Éligible (confirmé)
        </span>
      );
    }
    
    // Cas "À confirmer" = eligible mais pas toutes conditions, ou maybe
    if (status === "eligible" || status === "maybe") {
      const reasons = [...(missingFields || [])];
      if (isCalculable === false) reasons.push("Montant non calculable");
      if (amount === null && isCalculable !== false && status === "eligible") reasons.push("Montant en attente");
      
      return (
        <div className="group relative inline-block">
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full cursor-help">
            <AlertCircle className="h-3 w-3" /> À confirmer
          </span>
          {reasons.length > 0 && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-52 p-2 bg-slate-800 text-white text-xs rounded shadow-lg z-50">
              <p className="font-medium mb-1">À vérifier :</p>
              <ul className="list-disc list-inside space-y-0.5">
                {reasons.map((reason, i) => (
                  <li key={i}>{reason.replace(/_/g, ' ')}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }
    
    if (status === "not_eligible") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
          Non éligible
        </span>
      );
    }
    
    return null;
  };

  // Formatage monétaire avec gestion de null
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) {
      return "Non chiffrable";
    }
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
  };

  const slotsByDate = availableSlots.reduce((acc, slot) => {
    if (!acc[slot.date]) acc[slot.date] = [];
    acc[slot.date].push(slot);
    return acc;
  }, {});

  // CTA principal - bouton de rappel gratuit
  const MainCTAButton = ({ className = "", size = "default" }) => {
    if (lead?.wants_aid || lead?.appointment_id) {
      return (
        <div className={`flex items-center gap-2 text-[#1A4D2E] font-medium ${className}`}>
          <CheckCircle className="h-5 w-5" />
          <span>Demande enregistrée</span>
        </div>
      );
    }
    
    return (
      <Button
        onClick={handleWantAid}
        disabled={wantAidLoading}
        className={`bg-[#E07A5F] hover:bg-[#cc6b52] text-white font-semibold shadow-lg hover:shadow-xl transition-all ${
          size === "large" ? "h-14 px-8 text-lg" : "h-12 px-6"
        } ${className}`}
        data-testid="main-cta-btn"
      >
        {wantAidLoading ? (
          <span className="flex items-center gap-2">
            <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>
            En cours...
          </span>
        ) : (
          <>
            <Phone className="h-5 w-5 mr-2" />
            Être rappelé gratuitement
          </>
        )}
      </Button>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1A4D2E] border-t-transparent"></div>
      </div>
    );
  }

  if (!lead || !detailedResults) {
    return null;
  }

  const displayName = lead.first_name || lead.contact_first_name || lead.collectivity_name || lead.company_name;
  const hasRequestedCallback = lead.wants_aid || lead.appointment_id;

  return (
    <div className="min-h-screen bg-[#FAF9F6] pb-24 md:pb-8">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-home">
            <Leaf className="h-7 w-7 text-[#1A4D2E]" />
            <span className="text-xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              MesSubventions
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/particulier?edit=${leadId}`)}
              className="hidden sm:flex items-center gap-1.5 text-slate-600 hover:text-[#1A4D2E] border-slate-200"
              data-testid="btn-edit-form"
            >
              <Edit className="h-4 w-4" />
              Modifier
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const success = downloadResultsPDF(lead, detailedResults);
                if (success) {
                  toast.success("PDF téléchargé avec succès");
                } else {
                  toast.error("Erreur lors de la génération du PDF");
                }
              }}
              className="hidden sm:flex items-center gap-1.5 text-slate-600 hover:text-[#1A4D2E] border-slate-200"
              data-testid="btn-download-pdf"
            >
              <Download className="h-4 w-4" />
              Télécharger PDF
            </Button>
            <Link 
              to="/"
              className="text-slate-500 hover:text-[#1A4D2E] text-sm flex items-center gap-1 transition-colors"
              data-testid="back-home"
            >
              <ArrowLeft className="h-4 w-4" />
              Accueil
            </Link>
          </div>
        </div>
      </header>

      {/* Bandeau accompagnement gratuit */}
      <div className="bg-[#1A4D2E] text-white py-2.5">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap items-center justify-center gap-2 text-sm">
          <Gift className="h-4 w-4 flex-shrink-0" />
          <span className="font-medium">Accompagnement 100% gratuit</span>
          <span className="hidden sm:inline text-white/70">•</span>
          <span className="text-white/90 hidden sm:inline">Sans engagement</span>
          <button 
            onClick={() => setShowWhyFree(!showWhyFree)}
            className="underline underline-offset-2 text-white/80 hover:text-white transition-colors ml-1"
          >
            Pourquoi c'est gratuit ?
          </button>
        </div>
        
        {/* Explication "Pourquoi c'est gratuit" */}
        {showWhyFree && (
          <div className="bg-[#143d24] mt-2 py-3 animate-fade-in">
            <div className="max-w-6xl mx-auto px-4 text-sm text-white/90 text-center">
              Notre accompagnement est financé par les dispositifs d'aides et nos partenaires. 
              Vous ne payez aucun frais supplémentaire pour l'étude et le montage de votre dossier.
            </div>
          </div>
        )}
      </div>

      {/* Layout principal avec sidebar desktop */}
      <div className="max-w-6xl mx-auto px-4 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Contenu principal */}
          <main className="flex-1 min-w-0">
            {/* Success Header */}
            <div className="text-center mb-8 animate-fade-in">
              <div className="w-14 h-14 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="h-7 w-7 text-[#1A4D2E]" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#1D1D1D] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="result-title">
                Votre simulation est prête !
              </h1>
              <p className="text-slate-600">
                Bonjour {displayName}, voici l'estimation détaillée de vos aides.
              </p>
            </div>

            {/* Alerte si calcul incomplet */}
            {detailedResults.calculation_complete === false && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid="calculation-incomplete-alert">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Certaines aides ne sont pas chiffrables</p>
                  <p className="text-amber-700 mt-1">
                    {detailedResults.non_calculable_programs?.length > 0 
                      ? `${detailedResults.non_calculable_programs.join(', ')} nécessitent des informations complémentaires (devis, données techniques).`
                      : "Des données complémentaires sont nécessaires pour un calcul précis."}
                  </p>
                </div>
              </div>
            )}

            {/* Summary Cards - Redesigned with separation */}
            <div className="mb-8 space-y-4">
              {/* Carte principale - Récapitulatif avec écrêtement */}
              {(() => {
                // Calcul de l'écrêtement selon la catégorie (barèmes officiels 2026)
                // Source: thermiconseil.fr, laprimeenergie.fr
                const ecretementRates = { bleu: 1.00, jaune: 0.90, violet: 0.80, rose: 0.50 };
                const ecretementRate = ecretementRates[detailedResults.income_category] || 0.80;
                const maxAidesPossibles = detailedResults.total_estimated_cost * ecretementRate;
                
                // Séparer les types d'aides
                const subventions = (detailedResults.by_payer?.['ANAH'] || 0) + 
                                   (detailedResults.by_payer?.['Fournisseurs d\'énergie (CEE)'] || 0);
                const avantagesFiscaux = detailedResults.by_payer?.['Fiscalité (TVA)'] || 0;
                const totalBrut = subventions + avantagesFiscaux;
                
                // Appliquer l'écrêtement uniquement sur les subventions
                const subventionsEcretees = Math.min(subventions, maxAidesPossibles);
                const totalNet = subventionsEcretees + avantagesFiscaux;
                const resteACharge = Math.max(0, detailedResults.total_estimated_cost - subventionsEcretees);
                
                return (
                  <>
                    {/* Ligne 1: Total + Reste à charge + Score */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                      {/* Total Subventions (avec écrêtement) */}
                      <div className="bg-gradient-to-br from-[#1A4D2E] to-[#2d6b47] rounded-xl p-4 md:p-5 shadow-lg" data-testid="total-aid-card">
                        <div className="flex items-center gap-2 text-white/70 text-xs md:text-sm mb-1">
                          <Euro className="h-4 w-4" />
                          Subventions directes
                        </div>
                        <p className="text-2xl md:text-3xl font-bold text-white">{formatCurrency(subventionsEcretees)}</p>
                        <p className="text-xs text-white/60 mt-1">
                          versées sur votre compte
                        </p>
                        {subventions > subventionsEcretees && (
                          <p className="text-xs text-amber-300 mt-1 flex items-center gap-1">
                            <Info className="h-3 w-3" />
                            Écrêté (max {Math.round(ecretementRate * 100)}% du coût)
                          </p>
                        )}
                      </div>

                      {/* Reste à Charge */}
                      <div className="bg-white rounded-xl p-4 md:p-5 shadow-sm border border-slate-100" data-testid="remaining-cost-card">
                        <div className="flex items-center gap-2 text-slate-500 text-xs md:text-sm mb-1">
                          <Building2 className="h-4 w-4" />
                          Reste à charge
                        </div>
                        <p className="text-2xl md:text-3xl font-bold text-[#E07A5F]">{formatCurrency(resteACharge)}</p>
                        <p className="text-xs text-slate-500 mt-1">après subventions</p>
                        {detailedResults.total_loans_available > 0 && (
                          <p className="text-xs text-blue-600 mt-2">
                            Finançable via Éco-PTZ
                          </p>
                        )}
                      </div>

                      {/* Score Card */}
                      <div className="col-span-2 md:col-span-1 bg-white rounded-xl p-4 md:p-5 shadow-sm border border-slate-100" data-testid="score-card">
                        <div className="flex items-center justify-between md:flex-col md:items-start">
                          <div>
                            <div className="flex items-center gap-2 text-slate-500 text-xs md:text-sm mb-1">
                              {getScoreIcon(detailedResults.aid_score_label)}
                              Profil d'éligibilité
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded-full text-white text-sm font-medium ${getScoreColor(detailedResults.aid_score_label)}`}>
                                {detailedResults.income_category?.toUpperCase()}
                              </span>
                              <span className="text-lg font-bold text-slate-700">{detailedResults.aid_score}/100</span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs text-slate-500 hover:text-[#1A4D2E] p-1 h-auto"
                            onClick={() => {
                              setIncomeForm({
                                income_rfr: lead.income_rfr || "",
                                household_size: lead.household_size || ""
                              });
                              setIncomeModalOpen(true);
                            }}
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Modifier mes revenus
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Ligne 2: Détail des aides */}
                    <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Détail de vos aides
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Colonne 1: Subventions */}
                        <div className="bg-emerald-50 rounded-lg p-3">
                          <h4 className="text-xs font-medium text-emerald-800 mb-2 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Subventions (versées directement)
                          </h4>
                          <div className="space-y-2">
                            {(detailedResults.by_payer?.['ANAH'] || 0) > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-600">MaPrimeRénov' (ANAH)</span>
                                <span className="text-sm font-bold text-emerald-700">{formatCurrency(detailedResults.by_payer['ANAH'])}</span>
                              </div>
                            )}
                            {(detailedResults.by_payer?.['Fournisseurs d\'énergie (CEE)'] || 0) > 0 && (
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-600">Prime CEE</span>
                                <span className="text-sm font-bold text-emerald-700">{formatCurrency(detailedResults.by_payer['Fournisseurs d\'énergie (CEE)'])}</span>
                              </div>
                            )}
                            <div className="border-t border-emerald-200 pt-2 mt-2">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-emerald-800">Total subventions</span>
                                <span className="text-sm font-bold text-emerald-800">{formatCurrency(subventionsEcretees)}</span>
                              </div>
                              {subventions > subventionsEcretees && (
                                <p className="text-xs text-amber-600 mt-1">
                                  ⚠️ Écrêté de {formatCurrency(subventions)} à {formatCurrency(subventionsEcretees)} (max {Math.round(ecretementRate * 100)}%)
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Colonne 2: Avantages fiscaux + Prêts */}
                        <div className="space-y-3">
                          {/* Avantages fiscaux */}
                          <div className="bg-blue-50 rounded-lg p-3">
                            <h4 className="text-xs font-medium text-blue-800 mb-2 flex items-center gap-1">
                              <Gift className="h-3 w-3" />
                              Avantages fiscaux (économie sur facture)
                            </h4>
                            <div className="space-y-2">
                              {avantagesFiscaux > 0 && (
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-slate-600">TVA réduite 5,5%</span>
                                  <span className="text-sm font-bold text-blue-700">{formatCurrency(avantagesFiscaux)}</span>
                                </div>
                              )}
                              <p className="text-xs text-blue-600">
                                Appliquée automatiquement sur la facture
                              </p>
                            </div>
                          </div>

                          {/* Prêts disponibles */}
                          {detailedResults.total_loans_available > 0 && (
                            <div className="bg-purple-50 rounded-lg p-3">
                              <h4 className="text-xs font-medium text-purple-800 mb-2 flex items-center gap-1">
                                <Euro className="h-3 w-3" />
                                Prêt à taux zéro disponible
                              </h4>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-600">Éco-PTZ</span>
                                <span className="text-sm font-bold text-purple-700">jusqu'à {formatCurrency(detailedResults.total_loans_available)}</span>
                              </div>
                              <p className="text-xs text-purple-600 mt-1">
                                Pour financer votre reste à charge
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Récapitulatif final */}
                      <div className="mt-4 pt-3 border-t border-slate-200">
                        <div className="flex flex-wrap justify-between items-center gap-2 text-sm">
                          <div className="flex items-center gap-4">
                            <span className="text-slate-600">
                              Coût travaux estimé: <strong>{formatCurrency(detailedResults.total_estimated_cost)}</strong>
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-emerald-700 font-medium">
                              - {formatCurrency(subventionsEcretees)} subventions
                            </span>
                            <span className="text-slate-400">=</span>
                            <span className="text-[#E07A5F] font-bold">
                              {formatCurrency(resteACharge)} à charge
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Explication de l'écrêtement - Repliable */}
                      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
                        <button 
                          onClick={() => setShowEcretementInfo(!showEcretementInfo)}
                          className="w-full p-4 flex items-center justify-between text-left hover:bg-amber-100/50 transition-colors"
                        >
                          <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                            <Info className="h-4 w-4" />
                            Comment fonctionne le plafonnement des aides ?
                          </h4>
                          {showEcretementInfo ? (
                            <ChevronUp className="h-5 w-5 text-amber-600" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-amber-600" />
                          )}
                        </button>
                        
                        {showEcretementInfo && (
                          <div className="px-4 pb-4 text-sm text-amber-900 space-y-2 border-t border-amber-200">
                            <p className="pt-3">
                              L'<strong>écrêtement</strong> fixe un <strong>plafond maximum</strong> : le cumul de toutes vos aides ne peut pas dépasser un certain pourcentage du coût des travaux.
                            </p>
                            <p className="text-amber-700 bg-white/50 p-2 rounded text-xs">
                              ⚠️ <strong>Attention :</strong> Ce plafond ne garantit pas que vous atteindrez ce pourcentage. Il limite simplement le maximum possible si vos aides dépassaient ce seuil.
                            </p>
                            <div className="bg-white/50 rounded p-3 mt-2">
                              <p className="font-medium mb-2">Plafonds par catégorie de revenus :</p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className={`p-2 rounded ${detailedResults.income_category === 'bleu' ? 'bg-blue-100 border-2 border-blue-400' : 'bg-slate-100'}`}>
                                  <span className="font-bold text-blue-700">Bleu</span> : max 100%
                                </div>
                                <div className={`p-2 rounded ${detailedResults.income_category === 'jaune' ? 'bg-yellow-100 border-2 border-yellow-400' : 'bg-slate-100'}`}>
                                  <span className="font-bold text-yellow-700">Jaune</span> : max 90%
                                </div>
                                <div className={`p-2 rounded ${detailedResults.income_category === 'violet' ? 'bg-purple-100 border-2 border-purple-400' : 'bg-slate-100'}`}>
                                  <span className="font-bold text-purple-700">Violet</span> : max 80%
                                </div>
                                <div className={`p-2 rounded ${detailedResults.income_category === 'rose' ? 'bg-pink-100 border-2 border-pink-400' : 'bg-slate-100'}`}>
                                  <span className="font-bold text-pink-700">Rose</span> : max 50%
                                </div>
                              </div>
                            </div>
                            <div className="mt-3 p-3 bg-white/50 rounded">
                              <p className="font-medium text-amber-800">Votre situation (profil {detailedResults.income_category?.toUpperCase()}) :</p>
                              <ul className="mt-1 space-y-1 text-amber-800">
                                <li>• Coût des travaux : <strong>{formatCurrency(detailedResults.total_estimated_cost)}</strong></li>
                                <li>• Plafond max ({Math.round(ecretementRate * 100)}%) : <strong>{formatCurrency(maxAidesPossibles)}</strong></li>
                                <li>• Vos aides calculées : <strong>{formatCurrency(subventions)}</strong></li>
                                <li>• Taux de couverture : <strong>{Math.round((subventions / detailedResults.total_estimated_cost) * 100)}%</strong></li>
                                {subventions > maxAidesPossibles ? (
                                  <li className="text-red-700 font-medium">
                                    ⚠️ Écrêtement appliqué : vos aides sont réduites à {formatCurrency(subventionsEcretees)}
                                  </li>
                                ) : (
                                  <li className="text-emerald-700">
                                    ✓ Pas d'écrêtement nécessaire (vos aides sont sous le plafond)
                                  </li>
                                )}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Explication du Reste à charge - Repliable */}
                      <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg overflow-hidden">
                        <button 
                          onClick={() => setShowResteChargeInfo(!showResteChargeInfo)}
                          className="w-full p-4 flex items-center justify-between text-left hover:bg-orange-100/50 transition-colors"
                        >
                          <h4 className="text-sm font-semibold text-orange-800 flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            Qu'est-ce que le reste à charge ?
                          </h4>
                          {showResteChargeInfo ? (
                            <ChevronUp className="h-5 w-5 text-orange-600" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-orange-600" />
                          )}
                        </button>
                        
                        {showResteChargeInfo && (
                          <div className="px-4 pb-4 text-sm text-orange-900 space-y-2 border-t border-orange-200">
                            <p className="pt-3">
                              Le <strong>reste à charge</strong> est la somme que vous devrez payer de votre poche pour réaliser vos travaux, après déduction des subventions.
                            </p>
                            
                            <div className="bg-white/60 rounded-lg p-3 mt-3">
                              <p className="font-medium text-orange-800 mb-2">Calcul de votre reste à charge :</p>
                              <div className="space-y-2">
                                <div className="flex justify-between items-center py-1 border-b border-orange-200">
                                  <span>Coût estimé des travaux</span>
                                  <span className="font-bold">{formatCurrency(detailedResults.total_estimated_cost)}</span>
                                </div>
                                <div className="flex justify-between items-center py-1 border-b border-orange-200 text-emerald-700">
                                  <span>- Subventions (versées sur votre compte)</span>
                                  <span className="font-bold">- {formatCurrency(subventionsEcretees)}</span>
                                </div>
                                <div className="flex justify-between items-center py-1 text-[#E07A5F] text-lg">
                                  <span className="font-medium">= Reste à charge</span>
                                  <span className="font-bold">{formatCurrency(resteACharge)}</span>
                                </div>
                              </div>
                            </div>

                            <div className="mt-3 p-3 bg-white/60 rounded-lg">
                              <p className="font-medium text-orange-800 mb-2">💡 Comment financer votre reste à charge ?</p>
                              <ul className="space-y-2 text-orange-800">
                                {detailedResults.total_loans_available > 0 && (
                                  <li className="flex items-start gap-2">
                                    <span className="text-blue-600 font-bold">1.</span>
                                    <div>
                                      <strong className="text-blue-700">Éco-PTZ (prêt à taux zéro)</strong>
                                      <p className="text-xs text-slate-600">Jusqu'à {formatCurrency(detailedResults.total_loans_available)} sans intérêts sur 20 ans</p>
                                    </div>
                                  </li>
                                )}
                                <li className="flex items-start gap-2">
                                  <span className="text-emerald-600 font-bold">{detailedResults.total_loans_available > 0 ? '2.' : '1.'}</span>
                                  <div>
                                    <strong className="text-emerald-700">Épargne personnelle</strong>
                                    <p className="text-xs text-slate-600">Vos économies ou placements</p>
                                  </div>
                                </li>
                                <li className="flex items-start gap-2">
                                  <span className="text-purple-600 font-bold">{detailedResults.total_loans_available > 0 ? '3.' : '2.'}</span>
                                  <div>
                                    <strong className="text-purple-700">Prêt travaux classique</strong>
                                    <p className="text-xs text-slate-600">Auprès de votre banque (taux variable)</p>
                                  </div>
                                </li>
                              </ul>
                            </div>

                            {avantagesFiscaux > 0 && (
                              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <p className="text-sm text-blue-800">
                                  <strong>💰 Bonus :</strong> La TVA réduite ({formatCurrency(avantagesFiscaux)}) est directement appliquée sur votre facture artisan. C'est une économie en plus !
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Bouton PDF mobile */}
            <div className="sm:hidden mb-6">
              <Button
                variant="outline"
                onClick={() => {
                  const success = downloadResultsPDF(lead, detailedResults);
                  if (success) {
                    toast.success("PDF téléchargé avec succès");
                  } else {
                    toast.error("Erreur lors de la génération du PDF");
                  }
                }}
                className="w-full flex items-center justify-center gap-2 text-slate-600 border-slate-200"
                data-testid="btn-download-pdf-mobile"
              >
                <Download className="h-4 w-4" />
                Télécharger la synthèse en PDF
              </Button>
            </div>

            {/* Encart simplifié de la catégorie de revenus */}
            <div className="mb-6 p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between" data-testid="income-info-box">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Catégorie de revenus :</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  detailedResults.income_category === 'bleu' ? 'bg-blue-100 text-blue-700' :
                  detailedResults.income_category === 'jaune' ? 'bg-yellow-100 text-yellow-700' :
                  detailedResults.income_category === 'violet' ? 'bg-purple-100 text-purple-700' :
                  'bg-pink-100 text-pink-700'
                }`}>
                  {detailedResults.income_category?.toUpperCase()}
                </span>
                <span className="text-xs text-slate-500">
                  ({formatCurrency(lead.income_rfr)} • {lead.household_size} pers.)
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={openIncomeModal}
                className="text-xs h-7 px-3 border-slate-300 text-slate-600 hover:bg-slate-100"
                data-testid="btn-verify-income"
              >
                Vérifier / Modifier
              </Button>
            </div>

            {/* Modal de modification des revenus */}
            <Dialog open={incomeModalOpen} onOpenChange={setIncomeModalOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Edit className="h-5 w-5 text-[#1A4D2E]" />
                    Vérifier mes informations de revenus
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  {/* Explication */}
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-800 font-medium mb-2">⚠️ Comment remplir correctement</p>
                    <ul className="text-xs text-amber-700 space-y-1">
                      <li>• <strong>RFR</strong> : Revenu Fiscal de Référence sur votre dernier avis d'imposition</li>
                      <li>• <strong>Nombre de personnes</strong> : vous + conjoint + enfants rattachés <strong>(même sans revenus)</strong></li>
                      <li>• Les enfants mineurs ou majeurs rattachés fiscalement comptent dans le foyer</li>
                    </ul>
                  </div>

                  {/* Formulaire */}
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="income_rfr" className="text-sm font-medium">
                        Revenu Fiscal de Référence (€) *
                      </Label>
                      <Input
                        id="income_rfr"
                        type="number"
                        value={incomeForm.income_rfr}
                        onChange={(e) => setIncomeForm({ ...incomeForm, income_rfr: e.target.value })}
                        placeholder="Ex: 35000"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="household_size" className="text-sm font-medium">
                        Nombre de personnes dans le foyer fiscal *
                      </Label>
                      <Input
                        id="household_size"
                        type="number"
                        min="1"
                        max="10"
                        value={incomeForm.household_size}
                        onChange={(e) => setIncomeForm({ ...incomeForm, household_size: e.target.value })}
                        placeholder="Ex: 3"
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Incluez les enfants rattachés, même s'ils n'ont pas de revenus
                      </p>
                    </div>
                  </div>

                  {/* Boutons */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setIncomeModalOpen(false)}
                      className="flex-1"
                    >
                      Annuler
                    </Button>
                    <Button
                      onClick={handleIncomeUpdate}
                      disabled={incomeUpdateLoading}
                      className="flex-1 bg-[#1A4D2E] hover:bg-[#153d25]"
                    >
                      {incomeUpdateLoading ? "Mise à jour..." : "Recalculer mon éligibilité"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* ============================================ */}
            {/* SECTION 1: VOS TRAVAUX SÉLECTIONNÉS */}
            {/* ============================================ */}
            {detailedResults.requested_works && detailedResults.requested_works.length > 0 && (
              <section className="mb-10" data-testid="requested-works-section">
                <div className="bg-white rounded-2xl shadow-lg border-2 border-[#1A4D2E]/20 overflow-hidden">
                  <div className="bg-gradient-to-r from-[#1A4D2E] to-[#2d6b47] px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                        <ClipboardList className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          Vos travaux sélectionnés
                        </h2>
                        <p className="text-white/70 text-xs">{detailedResults.requested_works.length} travaux • Catégorie {detailedResults.income_category}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="divide-y divide-slate-100">
                  {detailedResults.requested_works.map((work, index) => (
                    <div 
                      key={work.work_type}
                      className={`p-4 ${index > 0 ? 'border-t border-slate-100' : ''}`}
                      data-testid={`requested-work-${work.work_type}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <span className="w-7 h-7 rounded-full bg-[#1A4D2E] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                            {index + 1}
                          </span>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-[#1D1D1D]">{work.label}</h3>
                              {work.technical_code && (
                                <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded font-mono">{work.technical_code}</span>
                              )}
                            </div>
                            {work.description && (
                              <p className="text-sm text-slate-500 mt-0.5">{work.description}</p>
                            )}
                            {/* Nouveau: Affichage du coût estimé et du détail de calcul */}
                            {work.estimated_cost && (
                              <div className="mt-2 text-xs text-slate-600 bg-slate-50 rounded px-2 py-1 inline-block">
                                <span className="font-medium">Coût estimé:</span> {formatCurrency(work.estimated_cost)}
                                {work.cost_calculation && (
                                  <span className="text-slate-400 ml-2">({work.cost_calculation.split('=')[0].trim()})</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {work.eligible_aids && work.eligible_aids.length > 0 ? (
                            <div className="space-y-1">
                              {work.eligible_aids.map((aid, i) => (
                                <div key={i} className="flex items-center justify-end gap-2">
                                  <span className="text-xs text-slate-500">{aid.name}</span>
                                  <span className="text-sm font-bold text-[#1A4D2E] bg-[#E8F5E9] px-2 py-0.5 rounded">{aid.display}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 italic">Sur devis</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
              </section>
            )}

            {/* ============================================ */}
            {/* SECTION 2: MINI-AUDIT DÉTAILLÉ (NOUVEAUTÉ 2026) */}
            {/* ============================================ */}
            <section className="mb-10" data-testid="mini-audit-section">
              <MiniAuditSection 
                leadId={lead.id}
                workTypes={lead.work_types || []}
                incomeCategory={detailedResults.income_category}
                department={lead.department}
                housingSurface={lead.housing_surface}
                onAuditComplete={(data) => {
                  console.log("Mini-audit complété:", data);
                  toast.success("Mini-audit sauvegardé !");
                }}
              />
            </section>

            {/* ============================================ */}
            {/* SECTION 3: MONO-GESTE VS RÉNO D'AMPLEUR */}
            {/* ============================================ */}
            <section className="mb-10" data-testid="reno-comparison-section">
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-lg overflow-hidden">
                <div className="px-5 py-4 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-yellow-400/20 flex items-center justify-center">
                      <Sparkles className="h-5 w-5 text-yellow-400" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Quel parcours choisir ?
                      </h2>
                      <p className="text-white/60 text-xs">Comparez les deux options disponibles</p>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <RenoComparisonSection 
                    workTypes={lead.work_types || []}
                    incomeCategory={detailedResults.income_category}
                    housingAge={lead.housing_age}
                  />
                </div>
              </div>
            </section>


            {/* ============================================ */}
            {/* SECTION 4: MAPRIMEADAPT' (si applicable) */}
            {/* ============================================ */}
            {(lead.situation_handicap || lead.perte_autonomie || lead.age >= 60) && (() => {
              // MaPrimeAdapt' : taux selon catégorie de revenus
              // ATTENTION: Le taux dépend du RFR du foyer fiscal COMPLET à l'adresse des travaux
              // 70% pour revenus très modestes (équivalent Bleu)
              // 50% pour revenus modestes (équivalent Jaune)
              // Pas d'aide pour revenus supérieurs
              
              // On indique les deux taux possibles car on n'a pas la certitude
              // que le RFR renseigné est bien celui du foyer complet
              const estimatedRate = detailedResults.income_category === 'bleu' ? 70 : 
                                   detailedResults.income_category === 'jaune' ? 50 : null;
              
              return (
                <section className="mb-10" data-testid="maprimeadapt-section">
                  <div className="bg-white rounded-2xl shadow-lg border-2 border-purple-300 overflow-hidden">
                    <div className="bg-gradient-to-r from-purple-600 to-purple-500 px-5 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                            <Accessibility className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                              MaPrimeAdapt'
                            </h2>
                            <p className="text-white/70 text-xs">Aide à l'adaptation du logement</p>
                          </div>
                        </div>
                        <span className="text-xs px-3 py-1.5 bg-amber-400 text-amber-900 rounded-full font-semibold flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          À vérifier
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-5">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                        <div>
                          <p className="text-sm text-slate-600 mb-3">
                            Aide pour l'adaptation du logement au vieillissement ou au handicap.
                          </p>
                          <div className="flex items-center gap-4 flex-wrap">
                            <div>
                              <p className="text-xs text-slate-500">Taux de financement</p>
                              {estimatedRate ? (
                                <>
                                  <p className="text-2xl font-bold text-purple-700">{estimatedRate}%</p>
                                  <p className="text-xs text-amber-600">estimation catégorie {detailedResults.income_category}</p>
                                </>
                              ) : (
                                <>
                                  <p className="text-2xl font-bold text-purple-700">50% ou 70%</p>
                                  <p className="text-xs text-amber-600">selon revenus du foyer</p>
                                </>
                              )}
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Plafond travaux</p>
                              <p className="text-2xl font-bold text-purple-700">22 000 €</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Aide max possible</p>
                              {estimatedRate ? (
                                <>
                                  <p className="text-2xl font-bold text-purple-700">{(22000 * estimatedRate / 100).toLocaleString('fr-FR')} €</p>
                                  <p className="text-xs text-slate-400">({estimatedRate}% de 22 000€)</p>
                                </>
                            ) : (
                              <>
                                <p className="text-2xl font-bold text-purple-700">11 000 - 15 400 €</p>
                                <p className="text-xs text-slate-400">(50-70% de 22 000€)</p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button 
                        className="bg-purple-600 hover:bg-purple-700"
                        onClick={() => {
                          const mpaProgram = detailedResults.official_programs?.find(p => p.id === 'maprimeadapt');
                          if (mpaProgram) {
                            setCalculatorModal({ isOpen: true, program: mpaProgram });
                          }
                        }}
                      >
                        <Calculator className="h-4 w-4 mr-2" />
                        Calculer mon aide
                      </Button>
                    </div>

                    {/* Avertissement sur les conditions spécifiques */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                      <p className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1">
                        <AlertCircle className="h-3.5 w-3.5" />
                        Vérifications nécessaires
                      </p>
                      <ul className="text-xs text-amber-700 space-y-1 ml-4">
                        <li>• <strong>Revenus du foyer fiscal complet</strong> à l'adresse des travaux</li>
                        <li>• <strong>Âge ≥ 70 ans</strong>, ou <strong>60-69 ans avec GIR 1-6</strong>, ou <strong>handicap ≥ 50%</strong></li>
                        <li>• <strong>Résidence principale</strong> uniquement</li>
                      </ul>
                    </div>
                    
                    <div className="text-xs text-slate-600 bg-slate-50 rounded-lg p-3">
                      <p className="font-medium mb-1">Travaux éligibles :</p>
                      <p>Douche de plain-pied, monte-escalier, élargissement des portes, rampes...</p>
                    </div>
                  </div>
                  </div>
                </section>
              );
            })()}

            {/* ============================================ */}
            {/* SECTION 5: HISTORIQUE DES AIDES PERÇUES */}
            {/* ============================================ */}
            <section className="mb-10" data-testid="aid-history-section">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-[#1D1D1D]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Enveloppe disponible
                    </h2>
                    <p className="text-amber-700 text-xs">Déclarez vos aides déjà perçues</p>
                  </div>
                </div>
                <AidHistorySection incomeCategory={detailedResults.income_category} />
              </div>
            </section>

            {/* ============================================ */}
            {/* SECTION 6: DÉTAIL DES AIDES */}
            {/* ============================================ */}
            <section className="mb-10" data-testid="available-aids-section">
              <div className="bg-white rounded-2xl shadow-lg border-2 border-blue-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                      <Euro className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Détail des aides
                      </h2>
                      <p className="text-white/70 text-xs">{detailedResults.official_programs?.length || 0} dispositifs disponibles</p>
                    </div>
                  </div>
                </div>

                <div className="p-5">
                  {/* Pack Recommandé */}
                  {detailedResults.recommended_bundle && detailedResults.recommended_bundle.total_amount > 0 && (
                    <div className="mb-6" data-testid="recommended-bundle-section">
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <Gift className="h-4 w-4" />
                        Pack recommandé
                      </h3>
                      
                      <div className="bg-gradient-to-br from-[#1A4D2E] to-[#2d6b47] rounded-xl p-4 md:p-5 text-white shadow-lg">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                          <div>
                            <p className="font-semibold text-white mb-1">{detailedResults.recommended_bundle.name}</p>
                            <p className="text-sm text-white/80">{detailedResults.recommended_bundle.description}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-white/70">Total des aides cumulées</p>
                            <p className="text-3xl font-bold text-white">
                              {formatCurrency(detailedResults.recommended_bundle.total_amount)}
                            </p>
                          </div>
                  </div>
                        
                        <div className="flex flex-wrap gap-2">
                          {detailedResults.recommended_bundle.included_programs?.map((prog, i) => (
                            <span key={i} className="text-xs px-3 py-1.5 bg-white/20 text-white rounded-full">
                              {prog.name}: <strong>{formatCurrency(prog.amount)}</strong>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Dispositifs Officiels */}
                  <div data-testid="official-programs">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Tous les dispositifs
                    </h3>
                    
                    <div className="space-y-3">
                      {detailedResults.official_programs.map((program) => (
                        <div 
                          key={program.id} 
                          className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden"
                          data-testid={`program-${program.id}`}
                        >
                          <div 
                            className="p-4 cursor-pointer hover:bg-slate-100 transition-colors"
                            onClick={() => setExpandedProgram(expandedProgram === program.id ? null : program.id)}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                  <h4 className="font-semibold text-[#1D1D1D] text-sm">{program.name}</h4>
                                  {getEligibilityBadge(
                                    program.eligibility_status, 
                                    program.missing_fields,
                                    program.is_calculable,
                                    program.amount
                                  )}
                                </div>
                                <p className="text-xs text-slate-500">
                                  {program.payer}
                                  {program.valid_from && (
                                    <span className="ml-2 text-slate-400">
                                      • {program.valid_from} → {program.valid_to || "indéfini"}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="text-right flex-shrink-0 flex items-center gap-2">
                                <div className="text-right">
                                  {program.funding_rate ? (
                                    <p className="text-lg font-bold text-[#1A4D2E]">{program.funding_rate}%</p>
                                  ) : program.estimated_amount ? (
                                    <p className="text-lg font-bold text-[#1A4D2E]">{formatCurrency(program.estimated_amount)}</p>
                                  ) : (
                                    <p className="text-sm font-medium text-slate-500">À calculer</p>
                                  )}
                                  {program.max_ceiling && (
                                    <p className="text-xs text-slate-500">Plafond : {formatCurrency(program.max_ceiling)}</p>
                                  )}
                                </div>
                                <button className="text-slate-400 p-0.5">
                                  {expandedProgram === program.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Section détaillée */}
                          {expandedProgram === program.id && (
                            <div className="px-4 pb-4 border-t border-slate-200 pt-3 bg-white">
                              {program.eligibility_status !== "eligible" && (
                                <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                                  <p className="text-xs text-amber-800 font-medium flex items-start gap-1.5">
                                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                                    <span>
                                      <strong>Éligibilité à confirmer</strong> – Informations manquantes ou vérification requise.
                                    </span>
                                  </p>
                                </div>
                              )}

                              {program.is_calculable && (
                                <div className="mb-3">
                                  <Button
                                    size="sm"
                                    onClick={() => setCalculatorModal({ isOpen: true, program })}
                                    className="bg-[#1A4D2E] hover:bg-[#153d25] text-xs"
                                  >
                                    <Calculator className="h-3.5 w-3.5 mr-1.5" />
                                    Calculer mon aide avec mon devis
                                  </Button>
                                </div>
                              )}

                              <div className="grid md:grid-cols-2 gap-3">
                                <div>
                                  <h5 className="text-xs font-semibold text-slate-700 mb-1.5">
                                    Conditions d'éligibilité
                                  </h5>
                                  <ul className="text-xs text-slate-600 space-y-0.5">
                                    {(program.conditions || []).map((cond, i) => (
                                      <li key={i} className="flex items-start gap-1.5">
                                        <span className="text-[#1A4D2E]">•</span>
                                        <span>{cond}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                <div>
                                  <h5 className="text-xs font-semibold text-slate-700 mb-1.5">
                                    Documents requis
                                  </h5>
                                  <ul className="text-xs text-slate-600 space-y-0.5">
                                    {(program.required_documents || []).map((doc, i) => (
                                      <li key={i} className="flex items-start gap-1.5">
                                        <span className="text-[#E07A5F]">•</span>
                                        <span>{doc}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>

                              {program.source_url && (
                                <div className="mt-3 pt-2 border-t border-slate-200">
                                  <a 
                                    href={program.source_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs text-[#1A4D2E] hover:underline flex items-center gap-1"
                                  >
                                    <ExternalLink className="h-3 w-3" /> En savoir plus
                                  </a>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Disclaimer */}
            <p className="text-center text-xs text-slate-500 mb-8">
              Simulation indicative. Les montants définitifs dépendent de l'étude de votre dossier.
            </p>
          </main>

          {/* Sidebar fixe desktop */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24">
              <div className="bg-white rounded-xl shadow-md border border-slate-100 p-5" data-testid="desktop-sidebar-cta">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Phone className="h-6 w-6 text-[#1A4D2E]" />
                  </div>
                  <h3 className="font-bold text-[#1D1D1D] mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Besoin d'aide ?
                  </h3>
                  <p className="text-sm text-slate-600">
                    Un conseiller vous accompagne gratuitement
                  </p>
                </div>

                {hasRequestedCallback ? (
                  <div className="bg-[#E8F5E9] rounded-lg p-4 text-center">
                    <CheckCircle className="h-8 w-8 text-[#1A4D2E] mx-auto mb-2" />
                    <p className="font-medium text-[#1A4D2E] text-sm">Demande enregistrée !</p>
                    <p className="text-xs text-slate-600 mt-1">Nous vous rappelons très bientôt</p>
                  </div>
                ) : (
                  <>
                    <MainCTAButton className="w-full mb-3" />
                    <ul className="space-y-2 text-xs text-slate-600">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3.5 w-3.5 text-[#1A4D2E]" />
                        100% gratuit, sans engagement
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3.5 w-3.5 text-[#1A4D2E]" />
                        Étude personnalisée de votre dossier
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3.5 w-3.5 text-[#1A4D2E]" />
                        Montage administratif inclus
                      </li>
                    </ul>
                  </>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* CTA sticky mobile */}
      {!hasRequestedCallback && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 md:hidden z-40 shadow-[0_-4px_20px_rgba(0,0,0,0.1)]" data-testid="mobile-sticky-cta">
          <MainCTAButton className="w-full" size="large" />
          <p className="text-center text-xs text-slate-500 mt-1.5">
            100% gratuit • Sans engagement
          </p>
        </div>
      )}

      {/* Modal de calcul d'aide */}
      <AidCalculatorModal
        isOpen={calculatorModal.isOpen}
        onClose={() => setCalculatorModal({ isOpen: false, program: null })}
        program={calculatorModal.program}
        incomeCategory={detailedResults?.income_category}
        onCalculate={(programId, amount) => {
          setCalculatedAmounts(prev => ({ ...prev, [programId]: amount }));
          toast.success(`Estimation enregistrée : ${amount.toLocaleString('fr-FR')}€`);
        }}
      />
    </div>
  );
}
